/**
 * How long somebody has been away, in the only units a greeting cares about.
 *
 * The device clock is the entire input. Nothing here reaches for the network,
 * because the greeting is the first thing said after the tap: a person who
 * hears silence while a profile request resolves has already decided the
 * product is slow, and on the connections this is built for that pause is
 * seconds rather than milliseconds.
 *
 * `now` is a parameter everywhere rather than a `Date.now()` call inside these
 * functions. Reading the clock in here would make the month band reachable
 * only by a test that waits a month, or one that installs fake timers around
 * code that has nothing to do with time, so in practice it would be the branch
 * nobody runs until a real person has been away that long.
 */

/** The five distances the returning greeting distinguishes. */
export type ElapsedBand = 'moments' | 'sameDay' | 'newDay' | 'week' | 'month'

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night'

/**
 * Below this, the person is treated as still being in the same conversation.
 *
 * Twenty minutes covers being interrupted by a call, putting the phone down to
 * find the thing you were asked about, or the browser reloading the tab. It is
 * short enough that somebody who left after breakfast and came back after
 * lunch is not answered with "Yes?", which reads as though Namu believes it was
 * mid-sentence and is faintly impatient.
 */
export const MOMENTS_MS = 20 * 60 * 1000

const DAY_MS = 24 * 60 * 60 * 1000

/** Calendar days, not elapsed days. Seven sleeps is what "a week" means. */
const WEEK_DAYS = 7
const MONTH_DAYS = 30

function startOfLocalDay(at: number): number {
  const date = new Date(at)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

/**
 * Whole local days between two instants, counted from midnight to midnight.
 *
 * Dividing the elapsed milliseconds by a day would be simpler and wrong at the
 * boundary that matters most: somebody who spoke at eleven at night and came
 * back at eight the next morning is nine hours away and one sleep away, and
 * the greeting is about the sleep. Rounded rather than floored so an hour of
 * clock adjustment cannot turn one day into zero.
 */
export function calendarDaysApart(from: number, to: number): number {
  return Math.round((startOfLocalDay(to) - startOfLocalDay(from)) / DAY_MS)
}

/**
 * Which band a return falls into.
 *
 * The short band is measured in elapsed time and the rest in calendar days,
 * and the order of the two checks is the interesting part. Someone who spoke
 * at ten to midnight and came back at ten past is twenty minutes and one
 * calendar day away. Asking the calendar first greets them with "Good
 * morning", which is absurd to somebody who has not moved from their chair.
 *
 * A negative elapsed time is not impossible. A phone whose clock was wrong and
 * has since been corrected can report a last session in the future, and the
 * clamp keeps that case in the shortest band rather than letting a negative
 * day count fall through to "it has been a while" about something that has not
 * happened yet.
 */
export function elapsedBand(lastSpokeAt: number, now: number): ElapsedBand {
  const elapsed = Math.max(0, now - lastSpokeAt)
  if (elapsed < MOMENTS_MS) return 'moments'

  const days = calendarDaysApart(lastSpokeAt, now)
  if (days <= 0) return 'sameDay'
  if (days < WEEK_DAYS) return 'newDay'
  if (days < MONTH_DAYS) return 'week'
  return 'month'
}

/**
 * Which greeting the hour of the day calls for.
 *
 * Four parts rather than two. The first version split the day into morning and
 * everything else, which meant the time of day stopped saying anything at all
 * after noon, and most of the use of this product is after noon.
 *
 * The boundaries are drawn where the Hausa greetings change rather than where
 * an English clock does. `Ina kwana` runs until the late morning, `barka da
 * rana` covers the middle of the day, `barka da yamma` starts when the heat
 * drops rather than at any particular hour, and `barka da dare` is late. The
 * English side is then fitted to the same boundaries so that both languages
 * describe the same moment, which matters because a person can switch language
 * mid-conversation and must not be greeted into a different part of the day.
 */
export function timeOfDay(at: number): TimeOfDay {
  const hour = new Date(at).getHours()
  if (hour >= 5 && hour <= 11) return 'morning'
  if (hour >= 12 && hour <= 16) return 'afternoon'
  if (hour >= 17 && hour <= 21) return 'evening'
  return 'night'
}
